import { useTranslation } from 'react-i18next';

import { Button as ShButton, Input } from '../../../components/shadcn/index.js';

export type PeriodPreset = 'last7' | 'last30' | 'last90' | 'mtd' | 'qtd' | 'ytd' | 'custom';

export interface PeriodValue {
  preset: PeriodPreset;
  from: string;
  to: string;
}

const PRESETS: PeriodPreset[] = ['last7', 'last30', 'last90', 'mtd', 'qtd', 'ytd'];

/**
 * Compute (from, to) ISO date-only strings for a named preset, anchored
 * to the caller-supplied `today` so the picker is testable.
 */
export function rangeForPreset(preset: PeriodPreset, today = new Date()): { from: string; to: string } {
  const to = isoDate(today);
  const startOf = (year: number, month: number, day: number) =>
    isoDate(new Date(Date.UTC(year, month, day)));
  const y = today.getUTCFullYear();
  const m = today.getUTCMonth();
  switch (preset) {
    case 'last7': return { from: isoDate(addDays(today, -6)), to };
    case 'last30': return { from: isoDate(addDays(today, -29)), to };
    case 'last90': return { from: isoDate(addDays(today, -89)), to };
    case 'mtd': return { from: startOf(y, m, 1), to };
    case 'qtd': return { from: startOf(y, Math.floor(m / 3) * 3, 1), to };
    case 'ytd': return { from: startOf(y, 0, 1), to };
    case 'custom': return { from: to, to };
  }
}

function isoDate(d: Date): string {
  return d.toISOString().slice(0, 10);
}
function addDays(d: Date, days: number): Date {
  const x = new Date(d);
  x.setUTCDate(x.getUTCDate() + days);
  return x;
}

export interface PeriodPickerProps {
  value: PeriodValue;
  onChange: (next: PeriodValue) => void;
}

export function PeriodPicker({ value, onChange }: PeriodPickerProps): JSX.Element {
  const { t } = useTranslation('reports');
  const setPreset = (p: PeriodPreset) => {
    if (p === 'custom') {
      onChange({ ...value, preset: 'custom' });
      return;
    }
    const { from, to } = rangeForPreset(p);
    onChange({ preset: p, from, to });
  };
  return (
    <div className="flex flex-wrap items-end gap-2">
      <div className="flex flex-wrap gap-1 rounded-md border border-border bg-bg p-1">
        {PRESETS.map((p) => (
          <ShButton
            key={p}
            variant={value.preset === p ? 'default' : 'ghost'}
            size="sm"
            onClick={() => setPreset(p)}
            className="h-7 px-2 text-xs"
          >
            {t(`statistics.period.${p}`)}
          </ShButton>
        ))}
      </div>
      <div className="flex items-end gap-2">
        <label className="flex flex-col gap-1 text-xs text-fg-muted">
          <span>{t('statistics.period.from')}</span>
          <Input
            type="date"
            value={value.from}
            onChange={(e) => onChange({ ...value, preset: 'custom', from: e.target.value })}
            className="h-8 w-36"
          />
        </label>
        <label className="flex flex-col gap-1 text-xs text-fg-muted">
          <span>{t('statistics.period.to')}</span>
          <Input
            type="date"
            value={value.to}
            onChange={(e) => onChange({ ...value, preset: 'custom', to: e.target.value })}
            className="h-8 w-36"
          />
        </label>
      </div>
    </div>
  );
}
